import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { apiAction, pageContext } from 'ctm-util-functions';
import { PrefillWelcomeBackSplash } from 'ctm-components/components/anon_prefill/prefill_welcome_back_splash';
import { updateTokens, updateState } from 'ctm-components/components/prefill/utils';
import { getBrand } from 'ctm-app/store/selector';
import { getExcludedPagesForPrefill } from 'ctm-util-functions/lib/excludePages';
import { CarJourneySummary } from './prefill_car_journey_summary';

const _PrefillWelcomeBack = ({
    resetJourneyPayload,
    showWelcomeBack,
    userHasSavedResults,
    loadPrefillPayloadToState,
    verifyResponse,
    resetPrefillValues,
    trackWelcomeBackSplash,
    isManualJourney,
    setOfferDetails,
    setIsAnonPrefillOrphan,
    postPrefillAction,
    setShowWelcomeBack
}) => {
    const dispatch = useDispatch();
    const constructBrowserHistory = (flow, startPage, endPage) => {
        setShowWelcomeBack(false);
        const excludedPagesForPrefill = getExcludedPagesForPrefill();
        const nonEligibleHistory = ['edit_car_details', 'car_rego_state', 'confirmation', 'regular_driver', ...excludedPagesForPrefill];
        const schemaAsOrderedList = pageContext.getProps().models.pageSchemaModel.schemaAsOrderedList;
        const progressionModel = pageContext.getProps().models.progressionModel;
        if (!isManualJourney && flow.mode === 'continue') {
            window.history.pushState(null, '', '/car-insurance/journey/rego_state');
            return;
        }
        let history = schemaAsOrderedList.slice(schemaAsOrderedList.indexOf(startPage), schemaAsOrderedList.indexOf(endPage));
        if (history.length === 0) {
            history = ['start'];
        }
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < history.length; i++) {
            // Exclude pages to be excluded from history
            if (nonEligibleHistory.includes(history[i]) || progressionModel.isPageConditional(history[i])) {
                continue;
            }
            const route = `/car-insurance/journey/${history[i]}`;
            window.history.pushState(null, history[i], route);
        }
    };

    // Function to resume the quote from the welcome back splash.prefill_welcome_back_splash.js
    const resumeQuoteFunc = () => {
        const scrollToTop = () => {
            if (window.scrollY) {
                window.scrollTo({
                    top: 0,
                    behavior: 'instant'
                });
            }
        };
        scrollToTop();
        const apiSchema = {
            url: 'journey/initial',
            type: 'POST',
            requestAction: 'POST_PAYLOAD',
            requestPayload: 'journey',
            failureType: 'non-fatal'
        };
        setShowWelcomeBack(false);
        const currentState = pageContext.getProps().store.getState();
        const currentJourneyId = currentState.getIn(['user', 'journey', 'transaction', 'journeyId']);
        if (currentJourneyId) {
            setIsAnonPrefillOrphan(true);
            dispatch(apiAction(apiSchema, pageContext.getProps().pageState, [], apiSchema.baseUrlName, currentState));
            setIsAnonPrefillOrphan(null);
        }
        resetJourneyPayload();
        // Update tokens and state with prefill data.
        loadPrefillPayloadToState(verifyResponse.data, verifyResponse.data.prefill.accessToken, verifyResponse.data.prefill.refreshToken);
        if (verifyResponse.data.prefill.lastViewedPage === '/car-insurance/journey/results') {
            // Set offer details in state.
            setOfferDetails(verifyResponse);
            // Set userHasSavedResults to true in state.
            userHasSavedResults();
        }
        postPrefillAction();
        // Get pages before prefill page and populate browser history without routing
        const pageName = verifyResponse.data.prefill.lastViewedPage.split('/').pop();
        window.history.pushState(null, 'start', '/car-insurance/journey/prefill_check');
        constructBrowserHistory({ mode: 'resume' }, 'car_colour', pageName);
        pageContext.getProps().browserHistory.push(verifyResponse.data.prefill.lastViewedPage);
    };

    // Function to continue a new quote from the welcome back splash.
    const continueNewQuote = () => {
        sessionStorage.removeItem('last_viewed_page');
        resetPrefillValues();
        // Get pages before verify trigger page and populate browser history without routing
        window.history.pushState(null, 'start', '/car-insurance/journey/prefill_check');
        constructBrowserHistory({ mode: 'continue' }, 'start', 'car_colour');
        // Set next page based on Manual or Rego Lookup
        pageContext.getProps().browserHistory.push('/car-insurance/journey/car_colour');
    };

    if (window.dataLayer) {
        if (!showWelcomeBack && !window.dataLayer?.some((item) => item.actionStep === 'car welcome back')) {
            trackWelcomeBackSplash();
        }
    }
    const percentageComplete = pageContext.getProps().models.pageSchemaModel.getPercentageComplete(verifyResponse.data.prefill.lastViewedPage);

    return (
        <PrefillWelcomeBackSplash
            resumeQuoteFunc={resumeQuoteFunc}
            continueNewQuote={continueNewQuote}
            lastAccessedDate={verifyResponse?.data?.lastModifiedDate}
            autoResume={percentageComplete === 100}
        >
            <CarJourneySummary
                percentageComplete={percentageComplete}
            />
        </PrefillWelcomeBackSplash>
    );
};

// PropTypes for the PrefillWelcomeBack component.
const mapDispatchToProps = (dispatch) => ({
    setIsAnonPrefillOrphan: (value) => {
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'isAnonPrefillOrphan',
            value
        });
    },
    setOfferDetails: (verifyResponse) => {
        dispatch({ type: 'SET_OFFER_DETAILS', identifier: 'offers', payload: verifyResponse?.data?.offers });
    },
    postPrefillAction: () => {
        dispatch({
            type: 'PREFILL_DONE'
        });
    },
    trackWelcomeBackSplash: () => {
        dispatch({
            type: 'PREFILL_WELCOME_BACK'
        });
    },
    setShowWelcomeBack: (payload) => {
        dispatch({
            type: 'SET_SHOW_WELCOME_BACK',
            payload
        });
    },
    userHasSavedResults: () => {
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: true
        });
    },
    resetPrefillValues: () => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasBeenPrefilled',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasSeenResults',
            value: false
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        dispatch({
            type: 'RESET_RESULTS'
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.overnightParkingAddressLabel',
            value: null
        });
        dispatch({
            type: 'RESET_TOUCHES',
            stateKey: 'vertical.quote',
            identifiers: [
                'coverDetail.annualKilometres',
                'coverDetail.driver.dob',
                'coverDetail.driver.licenceAge',
                'coverDetail.overnightParking.address.postcode',
                'applicant.firstName',
                'applicant.lastName',
                'helpers.overnightParkingPostcode',
                'helpers.overnightParkingAddressLabel',
                'anyPreviousClaimsCount'
            ]
        });
    },
    resetJourneyPayload: () => {
        // Reset prefill values from any previous journeys within the session
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasBeenPrefilled',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'hasSeenResults',
            value: false
        });
        dispatch({
            type: 'SET_RESULTS_CONFIG_VALUE',
            key: 'helpers.userHasSavedResults',
            value: false
        });
        // Clear journey + form + results
        dispatch({
            type: 'SET_JOURNEY_PAYLOAD',
            payload: { }
        });
        dispatch({
            type: 'RESET_RESULTS'
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'anyPreviousClaimsCount',
            value: null
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'coverDetail',
            value: {
                useType: null,
                previousInsurer: null,
                requestedExcess: null,
                commencementDate: null,
                currentlyInsured: null,
                annualKilometres: null,
                driver: {
                    lastName: null,
                    gender: null,
                    claimsDetails: null,
                    licenceAge: null,
                    dob: null,
                    comprehensiveInsured: null,
                    comprehensiveInsuredYears: null,
                    anyPreviousClaims: null,
                    firstName: null,
                    employmentStatus: null
                },
                ownsHome: null,
                vehicle: {
                    body: null,
                    redbookCodeLong: null,
                    badge: null,
                    transmission: null,
                    nonStandardAccessories: null,
                    redbookCode: null,
                    modified: null,
                    model: null,
                    glassCode: null,
                    colour: null,
                    nviCode: null,
                    make: null,
                    state: null,
                    alarmFitted: null,
                    factoryOptions: null,
                    marketValue: null,
                    damaged: null,
                    fuel: null,
                    immobiliserFitted: null,
                    hasOtherAccessories: null,
                    rego: null,
                    description: null,
                    year: null,
                    securityFeatures: null
                },
                passengersPayment: null,
                goodsPayment: null,
                coverType: null,
                hasYoungerDriver: null,
                financeType: null,
                overnightParking: {
                    parkingType: null,
                    address: {
                        unitType: null,
                        unitNumber: null,
                        buildingName: null,
                        streetNumber: null,
                        streetName: null,
                        state: null,
                        suburb: null,
                        postcode: null
                    }
                },
                youngestDriver: {
                    licenceAge: null,
                    dob: null,
                    gender: null
                },
                driverOption: null,
                ownsAnotherCar: null
            }
        });
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'applicant',
            value: {
                optInPhone: null,
                optInEmail: null,
                optInPrivacy: null,
                email: null,
                phone: null,
                mobile: null,
                lastName: null,
                firstName: null
            }
        });
        // Clear form specifics
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.overnightParkingAddressLabel',
            value: null
        });
        dispatch({
            type: 'RESET_TOUCHES',
            stateKey: 'vertical.quote',
            identifiers: [
                'coverDetail.annualKilometres',
                'coverDetail.driver.dob',
                'coverDetail.driver.licenceAge',
                'coverDetail.overnightParking.address.postcode',
                'applicant.firstName',
                'applicant.lastName',
                'helpers.overnightParkingPostcode',
                'helpers.overnightParkingAddressLabel',
                'anyPreviousClaimsCount'
            ]
        });
    },
    loadPrefillPayloadToState: (responsePayload, accessToken, refreshToken) => {
        updateTokens(dispatch, accessToken, refreshToken);
        updateState(dispatch, responsePayload);
    }
});

_PrefillWelcomeBack.propTypes = {
    carMake: PropTypes.string,
    carModel: PropTypes.string
};

const mapStateToProps = (state) => {
    const safeSessionStorage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    return {
        apiResponsesModels: state.getIn(['vertical', 'quote', 'form', 'apiResponses', 'models']),
        apiResponsesMakes: state.getIn(['vertical', 'quote', 'apiResponses', 'makes']),
        registration: state.getIn(['vertical', 'quote', 'form', 'coverDetail', 'vehicle', 'registration']),

        anonId: state.getIn(['vertical', 'quote', 'form', 'helpers', 'prefill', 'userAnonymousId']) || safeSessionStorage?.getItem('user_anonymous_id'),
        journeyUrl: state.getIn(['serverConfig', 'journeyServiceBaseUrl']),
        env: state.getIn(['serverConfig', 'env']),
        brandCode: getBrand(state),
        verifyResponse: state.getIn(['vertical', 'quote', 'prefillVerify', 'response']).toJS(),
        showWelcomeBack: state.getIn(['vertical', 'quote', 'prefillVerify', 'showWelcomeBack']),
        isManualJourney: state.getIn(['page', 'isManualLookup'])
    };
};

const PrefillWelcomeBack = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PrefillWelcomeBack);

export { _PrefillWelcomeBack, PrefillWelcomeBack };
